import { clientToCssPixels } from '../../utils/displaySize';

/**
 * Where the tab strip sits, and how far it may travel.
 *
 * The strip is moved by a transform this component owns rather than by a real scroll box — see
 * the `--strip-offset` rule in the Tabs styles for why — so every writer of that offset, the
 * active-tab centring and the scroll gestures alike, measures through here.
 */

/** How long the strip takes to glide to a new offset, in ms. */
export const STRIP_GLIDE_MS = 220;

/** A gesture already tracks the pointer frame by frame, so its offsets land without a glide. */
export const STRIP_GLIDE_NONE = 0;

/**
 * How far the offset has to move to count as a new target. Re-measuring a strip that is mid-glide
 * returns rects that differ in their last decimal from one frame to the next, and writing those
 * back restarts the glide every frame, which stretches it out into a crawl.
 */
const STRIP_OFFSET_EPSILON = 0.5;

/** Distance from the strip's left edge to a tab's right edge, unaffected by the strip's offset. */
const resolveTabEnd = (tabList: HTMLElement, tab: HTMLElement) =>
  clientToCssPixels(tab.getBoundingClientRect().right - tabList.getBoundingClientRect().left);

/** Distance from the strip's left edge to the middle of a tab. */
export const resolveTabCenter = (tabList: HTMLElement, tab: HTMLElement) =>
  resolveTabEnd(tabList, tab) - clientToCssPixels(tab.getBoundingClientRect().width) / 2;

/**
 * Right edge of the last tab within the strip — where the scroll has to stop. Taken from the tab
 * rather than from scrollWidth, which also counts the list's trailing padding and would clamp the
 * scroll short of where rc-tabs puts it; the two writers then overwrite each other on every tab
 * change. Rects rather than offsetLeft for the same reason rc-tabs uses them: whole-pixel offsets
 * make the target drift between frames, and every drift restarts the glide.
 */
const resolveTabStripEnd = (tabList: HTMLElement) => {
  const tabs = tabList.querySelectorAll<HTMLElement>('.ant-tabs-tab');
  const lastTab = tabs[tabs.length - 1];
  return lastTab ? resolveTabEnd(tabList, lastTab) : tabList.scrollWidth;
};

export interface TabStripMetrics {
  tabList: HTMLElement;
  /** Strip width the overflow button does not cover. */
  visibleWidth: number;
  /** The most negative offset the strip may take: the last tab parked at the visible edge. */
  furthestOffset: number;
}

/** null until rc-tabs has rendered a strip to measure. */
export const readTabStripMetrics = (root: HTMLElement): TabStripMetrics | null => {
  const tabWrap = root.querySelector('.ant-tabs-nav-wrap');
  const tabList = root.querySelector<HTMLElement>('.ant-tabs-nav-list');
  if (!tabWrap || !tabList) return null;

  /* The overflow button only costs the strip room while it is actually on screen. Reserving it
     whenever the tabs overflow, or throwing the offset back to zero the moment rc-tabs hides it,
     both make a strip that sits within a chip's bold weight of fitting swing a button's width
     back and forth as that class flips. */
  const overflowButton = root.querySelector('.ant-tabs-nav-operations');
  const overflowButtonWidth =
    overflowButton && !overflowButton.classList.contains('ant-tabs-nav-operations-hidden')
      ? clientToCssPixels(overflowButton.getBoundingClientRect().width)
      : 0;
  const visibleWidth =
    clientToCssPixels(tabWrap.getBoundingClientRect().width) - overflowButtonWidth;

  return {
    tabList,
    visibleWidth,
    furthestOffset: Math.min(0, visibleWidth - resolveTabStripEnd(tabList)),
  };
};

/** Keeps the first tab pinned to the left edge and the last tab pinned to the right edge. */
export const clampStripOffset = (offset: number, furthestOffset: number) =>
  Math.min(0, Math.max(furthestOffset, offset));

/** NaN before the first offset is written, which is what marks it as a starting point. */
export const readStripOffset = (tabList: HTMLElement) =>
  Number.parseFloat(tabList.style.getPropertyValue('--strip-offset'));

/**
 * The strip reads its offset and glide from these, and the rule that consumes them is important,
 * so rc-tabs' own inline transform on the same node never takes effect. The very first offset is
 * where the strip starts rather than somewhere it moves to, so that one lands without a glide.
 */
export const applyStripOffset = (
  tabList: HTMLElement,
  offset: number,
  glideMs: number = STRIP_GLIDE_MS,
) => {
  const current = readStripOffset(tabList);
  if (Math.abs(current - offset) < STRIP_OFFSET_EPSILON) return;

  tabList.style.setProperty(
    '--strip-glide',
    `${Number.isFinite(current) ? glideMs : STRIP_GLIDE_NONE}ms`,
  );
  tabList.style.setProperty('--strip-offset', `${offset}px`);
};
